/**
 * Rules that belong to the whole workflow, not to one step: separation of
 * duties, holding an approved document until its effective date, and who is
 * kept informed without deciding anything.
 */

import { useEffect, useState } from 'react';
import { MultiSelect } from '../../shared/MultiSelect';
import { useUpdateWorkflow, type ApprovalWorkflow, type NotifyRoles } from './approvalsData';
import { ROLE_DISPLAY_NAMES } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

const WATCHERS = (['author', 'reviewer', 'approver', 'admin'] as const).map((r) => ({ id: r, name: ROLE_DISPLAY_NAMES[r] }));
const EVENTS: { key: keyof NotifyRoles; label: string }[] = [
  { key: 'submitted', label: 'When a document is submitted' },
  { key: 'published', label: 'When it is published' },
  { key: 'returned', label: 'When it is returned to the author' },
];
const same = (a: string[] = [], b: string[] = []) => [...a].sort().join('|') === [...b].sort().join('|');

export function WorkflowRules({ workflow, onSaved }: { workflow: ApprovalWorkflow; onSaved: (m: string) => void }) {
  const update = useUpdateWorkflow();
  const [notify, setNotify] = useState<NotifyRoles>(workflow.notifyRoles ?? {});
  const key = `${workflow.workflowId}|${JSON.stringify(workflow.notifyRoles ?? {})}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setNotify(workflow.notifyRoles ?? {}), [key]);
  const dirty = EVENTS.some((e) => !same(notify[e.key], workflow.notifyRoles?.[e.key]));

  return (
    <section className={styles.section} aria-labelledby="rules-title">
      <h2 className={styles.sectionTitle} id="rules-title">Rules for this workflow</h2>

      {/* These two are the governance switches on the page. A refused change
          used to flip the box back with nothing said, which reads as "saved"
          to anybody not watching closely. The boxes are controlled from the
          record, so the state stays honest; this says what happened. */}
      {update.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(update.error) || update.error instanceof Error ? update.error.message : 'That change could not be saved.'}
          {' '}Nothing was changed.
        </div>
      )}

      <label className={own.choice}>
        <input type="checkbox" id="rule-duties" checked={workflow.separationOfDuties !== false} disabled={update.isPending}
          onChange={(e) => update.mutate({ workflowId: workflow.workflowId, separationOfDuties: e.target.checked }, { onSuccess: () => onSaved(e.target.checked ? 'Separation of duties is on.' : 'Separation of duties is off for this workflow.') })} />
        <span>Separation of duties</span>
      </label>
      <p className={`${own.itemMeta} ${own.explain}`}>
        Prevents the submitter from approving, and prevents one person from approving more than one step, while
        another eligible person is available. If no alternative exists the rule relaxes, so an approval is never
        stranded. Eligible people may still return the document for changes either way.
      </p>

      <label className={own.choice} style={{ marginTop: 'var(--sp-4)' }}>
        <input type="checkbox" id="rule-hold" checked={workflow.holdUntilEffective === true} disabled={update.isPending}
          onChange={(e) => update.mutate({ workflowId: workflow.workflowId, holdUntilEffective: e.target.checked }, { onSuccess: () => onSaved(e.target.checked ? 'Approved documents will wait for their effective date.' : 'Documents will be published on the last approval.') })} />
        <span>Hold an approved document until its effective date</span>
      </label>
      <p className={`${own.itemMeta} ${own.explain}`}>
        After the final approval, a document with a future effective date stays unpublished until that date, when it
        is published on its own. With no future effective date it is published at once. An approver can publish it
        sooner from the approval record.
      </p>

      <h3 className={own.label} style={{ fontSize: 'var(--fs-md)', marginTop: 'var(--sp-5)' }}>Keep informed</h3>
      <p className={`${own.itemMeta} ${own.explain}`}>
        These roles are notified by email. They do not become approvers. Staff are told through acknowledgements,
        not here. Approval and publication are separate moments when a document is held for its effective date.
      </p>
      <div className={own.coverGrid}>
        {EVENTS.map((ev) => (
          <div key={ev.key}>
            <div className={own.label} aria-hidden="true">{ev.label}</div>
            <MultiSelect id={`notify-${ev.key}`} label={ev.label} options={WATCHERS} value={notify[ev.key] ?? []} onChange={(v) => setNotify({ ...notify, [ev.key]: v })} placeholder="No additional notification" disabled={update.isPending} />
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.btn} disabled={!dirty || update.isPending}
          onClick={() => update.mutate({ workflowId: workflow.workflowId, notifyRoles: notify }, { onSuccess: () => onSaved('Saved who is kept informed.') })}>
          Save who is kept informed
        </button>
      </div>
    </section>
  );
}
